import React, { useEffect, useMemo } from "react";
import { Stack, Box } from "@mui/material";
import { Formik, Form, FormikProps } from "formik";
import * as Yup from "yup";
import PaidIcon from "@mui/icons-material/Paid";

import FormAlert from "../components/common/FormAlert";
import FormTextField from "../components/common/FormTextField";
import FormSelectField from "../components/common/FormSelectField";
import SubmitButton from "../components/buttons/SubmitButton";

import { useAppDispatch, useAppSelector } from "../redux/store";
import { recordTransaction } from "../redux/actions/studentFeeLedgerActions";
import { getVoteHeads } from "../redux/actions/voteHeadActions";
import { studentFeeLedgerActions } from "../redux/slices/studentFeeLedger";
import { cleanFormData } from "../helpers/formHelpers";

import { TransactionType } from "../shared/enums/TransactionType";
import { PaymentMethod } from "../shared/enums/PaymentMethod";
import { StudentFeeLedgerRequestDto } from "../shared/dtos/studentFeeLedger/StudentFeeLedgerRequestDto";

const transactionTypeOptions = [
  { value: TransactionType.PAYMENT, label: "Payment (Credit)" },
  { value: TransactionType.INVOICE, label: "Manual Invoice (Debit)" },
  { value: TransactionType.WAIVER, label: "Waiver (Credit)" },
];

const paymentMethodOptions = [
  { value: PaymentMethod.MPESA, label: "M-Pesa" },
  { value: PaymentMethod.BANK_DEPOSIT, label: "Bank Deposit" },
  { value: PaymentMethod.CASH, label: "Cash" },
  { value: PaymentMethod.CHEQUE, label: "Cheque" },
];

const ledgerSchema = Yup.object().shape({
  transactionType: Yup.mixed<TransactionType>()
    .oneOf(Object.values(TransactionType))
    .required("Type is required"),
  amount: Yup.number()
    .required("Amount is required")
    .positive("Must be positive"),
  transactionDate: Yup.string().required("Date is required"),
  voteHeadId: Yup.string().when("transactionType", {
    is: TransactionType.INVOICE,
    then: (schema) => schema.required("Vote Head is required for Invoices"),
    otherwise: (schema) => schema.nullable().notRequired(),
  }),
  paymentMethod: Yup.mixed<PaymentMethod>().when("transactionType", {
    is: TransactionType.PAYMENT,
    then: (schema) => schema.required("Payment Method is required"),
    otherwise: (schema) => schema.nullable().notRequired(),
  }),
  referenceNumber: Yup.string().when("transactionType", {
    is: TransactionType.PAYMENT,
    then: (schema) => schema.required("Reference No. is required"),
    otherwise: (schema) => schema.nullable().notRequired(),
  }),
  narrative: Yup.string().required("Description is required"),
});

interface CreateStudentFeeLedgerFormProps {
  studentId: string;
  termId: string;
  loading: boolean;
  error: string | null;
}

const CreateStudentFeeLedgerForm: React.FC<CreateStudentFeeLedgerFormProps> = ({
  studentId,
  termId,
  loading,
  error,
}) => {
  const dispatch = useAppDispatch();
  const { items: voteHeads } = useAppSelector((state) => state.voteHead);

  useEffect(() => {
    if (!voteHeads || voteHeads.length === 0) {
      dispatch(getVoteHeads());
    }
  }, [dispatch, voteHeads]);

  const voteHeadOptions = useMemo(
    () => voteHeads?.map((v) => ({ value: v.id, label: v.name })) || [],
    [voteHeads],
  );

  const initialValues: StudentFeeLedgerRequestDto = {
    studentId,
    termId,
    transactionType: TransactionType.PAYMENT,
    amount: "" as unknown as number,
    transactionDate: new Date().toISOString().split("T")[0],
    voteHeadId: "",
    paymentMethod: PaymentMethod.BANK_DEPOSIT,
    referenceNumber: "",
    narrative: "School Fees Payment",
  };

  return (
    <Stack spacing={2} sx={{ width: "100%", textAlign: "center" }}>
      <FormAlert
        message={error}
        severity="error"
        closable
        onClose={() =>
          dispatch(studentFeeLedgerActions.setErrorBatchGeneration(null))
        }
      />
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={ledgerSchema}
        onSubmit={(values) => {
          const rawCleaned = cleanFormData(values) as Record<string, any>;
          const cleanedValues: StudentFeeLedgerRequestDto = {
            studentId: values.studentId,
            termId: values.termId,
            transactionType: values.transactionType,
            amount: Number(rawCleaned.amount),
            transactionDate: rawCleaned.transactionDate,
            narrative: rawCleaned.narrative,
            voteHeadId:
              values.transactionType === TransactionType.INVOICE
                ? rawCleaned.voteHeadId
                : null,
            paymentMethod:
              values.transactionType === TransactionType.PAYMENT
                ? (values.paymentMethod as PaymentMethod)
                : null,
            referenceNumber:
              values.transactionType === TransactionType.PAYMENT
                ? rawCleaned.referenceNumber
                : null,
          };

          dispatch(recordTransaction(cleanedValues));
        }}
      >
        {({
          values,
          setFieldValue,
          dirty,
          isValid,
        }: FormikProps<StudentFeeLedgerRequestDto>) => (
          <Form>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <FormSelectField
                name="transactionType"
                label="Transaction Type"
                options={transactionTypeOptions}
                onChange={(e) => {
                  const type = e.target.value as unknown as TransactionType;
                  setFieldValue("transactionType", type);
                  setFieldValue(
                    "narrative",
                    type === TransactionType.PAYMENT
                      ? "School Fees Payment"
                      : "Manual Invoice",
                  );
                }}
              />

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 2,
                }}
              >
                <FormTextField
                  name="amount"
                  label="Amount (KES)"
                  type="number"
                  slotProps={{ htmlInput: { min: 0 } }}
                />
                <FormTextField
                  name="transactionDate"
                  label="Date"
                  type="date"
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              </Box>

              {values.transactionType === TransactionType.INVOICE && (
                <FormSelectField
                  name="voteHeadId"
                  label="Vote Head"
                  options={voteHeadOptions}
                />
              )}

              {values.transactionType === TransactionType.PAYMENT && (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    gap: 2,
                  }}
                >
                  <FormSelectField
                    name="paymentMethod"
                    label="Payment Method"
                    options={paymentMethodOptions}
                  />
                  <FormTextField
                    name="referenceNumber"
                    label="Reference / Receipt No."
                    placeholder="e.g. QHX8194JZ"
                  />
                </Box>
              )}

              <FormTextField
                name="narrative"
                label="Narrative / Description"
                multiline
                rows={2}
              />

              <SubmitButton
                title="Save Transaction"
                loading={loading}
                loadingText="Saving..."
                disabled={!dirty || !isValid || loading}
                startIcon={<PaidIcon />}
                sx={{ mt: 1 }}
              />
            </Stack>
          </Form>
        )}
      </Formik>
    </Stack>
  );
};

export default CreateStudentFeeLedgerForm;
